import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, PhoneIcon, ClockIcon, SendIcon, CheckCircleIcon, FileSearchIcon, MessageSquareIcon, NetworkIcon, UserCheckIcon, GlobeIcon, MapIcon, ArrowRightIcon } from 'lucide-react';
const whyContactUs = [{
  icon: FileSearchIcon,
  title: 'Free Bill Analysis',
  desc: 'We review your current telecom expenses at no cost to find immediate savings.'
}, {
  icon: MessageSquareIcon,
  title: 'No Obligation Consultation',
  desc: 'Speak with our experts without any pressure or commitment to buy.'
}, {
  icon: NetworkIcon,
  title: '240+ Supplier Network',
  desc: 'Access to hundreds of providers ensures you get the best possible rates.'
}, {
  icon: UserCheckIcon,
  title: 'Dedicated Account Manager',
  desc: 'A single point of contact for all your telecom and IT needs.'
}];
const faqs = [{
  question: 'How long does a telecom audit take?',
  answer: 'Most initial audits are completed within 3-5 business days after we receive your current bills and contracts.'
}, {
  question: 'Is there a contract required?',
  answer: 'Our consulting and auditing services do not require long-term contracts. Supplier contracts will vary based on the services you choose.'
}, {
  question: 'What size businesses do you serve?',
  answer: 'We work with businesses of all sizes, from small local offices to large enterprises with multiple locations nationwide.'
}, {
  question: 'How quickly can I see savings?',
  answer: 'Many clients see savings on their very next billing cycle after we implement the recommended changes or switch providers.'
}, {
  question: 'Do you handle the transition to new providers?',
  answer: 'Yes! We manage the entire transition process, ensuring zero downtime and a seamless switch for your business operations.'
}];
export function ContactPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div data-id="element-1091">
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden bg-slate-900" data-id="element-1092">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1093"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1094"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1095"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1096">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1097">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1098">
              Let's Start
              <br className="hidden md:block" data-id="element-1099" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1100">
                Saving You Money
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-1101">
              For additional information on our initiatives or products, please
              fill out the requested information below and reference the product
              you would like to learn more about.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="py-20 bg-white" data-id="element-1102">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1103">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1104">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1105">
              Why Contact Ambient Telecom?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1106">
              We're here to help you optimize your communications and reduce
              your costs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1107">
            {whyContactUs.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-slate-50 p-8 rounded-2xl border border-slate-100 text-center hover:shadow-lg transition-shadow" data-id="element-1108">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-1109">
                  <item.icon className="w-7 h-7 text-primary" data-id="element-1110" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1111">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1112">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden" data-id="element-1113">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1114"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1115">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1116">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1117">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1118">
                <GlobeIcon className="w-4 h-4 mr-2 text-primary" data-id="element-1119" />
                Nationwide Coverage
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1120">
                Serving Businesses Across the Country
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-1121">
                Headquartered in the Baltimore DC Me, Maryland, we proudly serve the
                Mid-Atlantic region and businesses nationwide. No matter where
                your offices are located, our network of 240+ suppliers ensures
                we can connect you.
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-1122">
                {['Mid-Atlantic Region', 'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast'].map((region, i) => <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10" data-id="element-1123">
                    <MapIcon className="w-5 h-5 text-primary shrink-0" data-id="element-1124" />
                    <span className="font-medium" data-id="element-1125">{region}</span>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-1126">
              <img src="https://images.unsplash.com/photo-1508345228704-935cc84bf5e2?auto=format&fit=crop&q=80&w=800" alt="Map of service areas" className="w-full h-auto object-cover opacity-80" data-id="element-1127" />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/60 to-transparent" data-id="element-1128"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50" data-id="element-1129">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1130">
          <div className="text-center mb-16" data-id="element-1131">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1132">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1133">
              Common questions about working with Ambient Telecom.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1134">
            {faqs.map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-1135">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-1136">
                  <span className="font-bold text-slate-900 text-lg" data-id="element-1137">
                    {faq.question}
                  </span>
                  <div className={`transform transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-1138">
                    <ArrowRightIcon className="w-5 h-5 text-slate-400 rotate-90" data-id="element-1139" />
                  </div>
                </button>
                <AnimatePresence data-id="element-1140">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1141">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100 bg-slate-50/50 leading-relaxed" data-id="element-1142">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-1143">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1144">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl" data-id="element-1145">
            {/* Left - Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-900 p-10 md:p-14 flex flex-col justify-center" data-id="element-1146">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10" data-id="element-1147">
                Contact Us
              </h2>

              <div className="space-y-8" data-id="element-1148">
                <div className="flex items-start gap-4" data-id="element-1149">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1150">
                    <MapPinIcon className="w-5 h-5 text-primary" data-id="element-1151" />
                  </div>
                  <div data-id="element-1152">
                    <p className="text-white leading-relaxed" data-id="element-1153">
                      Century Plaza Office Center, 10632 Little Patuxent Pkwy.
                      Suite 302
                      <br data-id="element-1154" />
                      Columbia, MD 21044
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-1155">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1156">
                    <PhoneIcon className="w-5 h-5 text-primary" data-id="element-1157" />
                  </div>
                  <a href="tel:+4435451747" className="text-white hover:text-primary transition-colors font-medium" data-id="element-1158">
                    (443) 545-1747
                  </a>
                </div>

                <div className="flex items-start gap-4" data-id="element-1159">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1160">
                    <SendIcon className="w-5 h-5 text-primary" data-id="element-1161" />
                  </div>
                  <div className="flex flex-col space-y-1" data-id="element-1162">
                    <a href="mailto:info@ambienttelecom.com" className="text-white hover:text-primary transition-colors font-medium" data-id="element-1163">
                      info@ambienttelecom.com
                    </a>
                    <a href="mailto:support@ambienttelecom.com" className="text-white hover:text-primary transition-colors font-medium" data-id="element-1164">
                      support@ambienttelecom.com
                    </a>
                    <a href="mailto:ambienttelecoms@gmail.com" className="text-white hover:text-primary transition-colors font-medium" data-id="element-1165">
                      ambienttelecoms@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-1166">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1167">
                    <ClockIcon className="w-5 h-5 text-primary" data-id="element-1168" />
                  </div>
                  <p className="text-white" data-id="element-1169">
                    Mon – Fri – 9am – 5pm | Sat 9am – 5pm
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white p-10 md:p-14" data-id="element-1170">
              {submitted ? <div className="flex flex-col items-center justify-center h-full text-center py-10" data-id="element-1171">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-1172">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1173" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-1174">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600 mb-6 max-w-sm" data-id="element-1175">
                    Thank you for reaching out. Our team will get back to you
                    within 24 hours.
                  </p>
                  <button onClick={() => {
                setSubmitted(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  message: ''
                });
              }} className="text-primary font-semibold hover:underline" data-id="element-1176">
                    Send another message
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1177">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1178">
                    <div data-id="element-1179">
                      <label htmlFor="firstName" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-1180">
                        First Name
                      </label>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter your first name" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-1181" />
                    </div>
                    <div data-id="element-1182">
                      <label htmlFor="lastName" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-1183">
                        Last Name
                      </label>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter your last name" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-1184" />
                    </div>
                  </div>

                  <div data-id="element-1185">
                    <label htmlFor="email" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-1186">
                      Email
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email address" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-1187" />
                  </div>

                  <div data-id="element-1188">
                    <label htmlFor="phone" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-1189">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-1190" />
                  </div>

                  <div data-id="element-1191">
                    <label htmlFor="message" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-1192">
                      Message
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="How can we help you?" rows={5} required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-vertical" data-id="element-1193" />
                  </div>

                  <button type="submit" className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 px-10 rounded-lg transition-colors inline-flex items-center justify-center" data-id="element-1194">
                    Submit
                    <SendIcon className="w-4 h-4 ml-2" data-id="element-1195" />
                  </button>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}